import React from 'react';
import { roomVerbHint } from '../../utils/roomVerbs';

/**
 * 🪧 RoomPlate (09-17, Nina: "I'd like the notification to be more permanent
 * … a way to see who's in the room and actions specific to each room").
 *
 * The /room toast is a glance; this is what STAYS while the viewer is in any
 * room other than the main one: room name · who's here and what they're
 * doing · room-flavored shortcuts to commands that already exist · look
 * (which opens the room's picture since the 09-23 merge) · the way back. (09-18: the "rooms ▾" pill RETIRED — the door to the
 * pictures lives in the message box in every room, RoomPicker.RoomDoorButton.) Rendered IN FLOW directly above the
 * composer on both mounts (desktop + phone dock) — never absolute, so it can
 * never cover the feed's last lines.
 *
 * Pure presentation: every string arrives ready (utils/roomView builds them),
 * every action is a callback. Deliberately NO watcher count here (Nina:
 * keep it in /look, not on the plate). The main room renders no plate at all
 * — the caller simply doesn't mount it.
 *
 * 🪧 09-23 THE FOLD (MOVIE_NIGHT_UX_HANDOFF §3.2, Nina's ruling 8): during a
 * screening the plate folds to ONE line so the docked film gets the height.
 * `collapsed` = the handle: icon + name · the band text · the ONE chip named
 * by `keepVerb` (▶ Watch with the room un-seated, 🎬 Cinema seated) · the
 * arrow. Presence, the other chips, /look and the way back live behind
 * the arrow. `onToggle` given = the ▾/▴ arrow renders (expanded too); absent =
 * byte-identical to the plate before the fold existed. The page owns the
 * fold state (auto-fold once per screening, a manual un-fold sticks).
 */
const pill = {
  fontSize: 11.5, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
  border: '1px solid rgba(122,96,60,0.55)', background: 'rgba(47,35,24,0.9)',
  color: '#e6dac6', lineHeight: 1.5, whiteSpace: 'nowrap',
};
const verbPill = { ...pill, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 };

export default function RoomPlate({ icon, label, presence, note = null, band = null, verbs = [], onVerb, onLook, onBack, compact = false, collapsed = false, onToggle = null, keepVerb = null }) {
  const name = label ? label.charAt(0).toUpperCase() + label.slice(1) : 'This room';
  // 💡 a room verb says what it DOES (hover + screen reader); on touch the
  // same sentence shows in the composer's arg bar once the chip seeds it
  const chip = (v) => (
    <button key={v} type="button" style={verbPill} title={roomVerbHint(v) || 'Puts this in your message box'}
      {...(roomVerbHint(v) ? { 'aria-label': `${String(v).trim()}: ${roomVerbHint(v)}` } : {})}
      onClick={() => onVerb && onVerb(v)}>
      {v.trim()}
    </button>
  );
  const toggle = onToggle ? (
    <button type="button" data-plate-toggle={collapsed ? 'closed' : 'open'}
      aria-label={collapsed ? 'Unfold the room bar' : 'Fold the room bar'}
      title={collapsed ? 'Unfold the room bar' : 'Fold the room bar'}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onToggle}
      style={{ ...pill, padding: '0 8px', fontSize: 13, lineHeight: 1.6, flexShrink: 0 }}>
      {collapsed ? '▾' : '▴'}
    </button>
  ) : null;

  if (collapsed) {
    const want = keepVerb == null ? null : String(keepVerb).trim();
    const keep = want ? verbs.find((v) => String(v).trim() === want) : undefined;
    return (
      <div
        data-room-plate="1"
        data-plate-folded="1"
        style={{
          display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: compact ? 6 : 8,
          marginBottom: 8, height: compact ? 32 : 26, padding: compact ? '0 10px' : '0 12px',
          background: 'rgba(18,13,9,0.9)', border: '1px solid rgba(122,96,60,0.55)', borderRadius: 10,
          boxSizing: 'border-box', maxWidth: '100%', overflow: 'hidden', whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 800, color: '#e9b565', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', flex: '0 1 auto' }}>
          {icon ? `${icon} ` : ''}{name}
        </span>
        {band && band.text && (
          <span data-plate-band="1" style={{ fontSize: 11.5, fontWeight: 700, color: band.hot ? '#ffd98a' : '#e6dac6', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', flex: '0 1 auto' }}>
            · {band.text}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {keep !== undefined && chip(keep)}
        {toggle}
      </div>
    );
  }

  return (
    <div
      data-room-plate="1"
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: compact ? '5px 8px' : '6px 12px',
        marginBottom: 8, padding: compact ? '6px 10px' : '7px 12px',
        background: 'rgba(18,13,9,0.9)', border: '1px solid rgba(122,96,60,0.55)', borderRadius: 10,
        boxSizing: 'border-box', maxWidth: '100%',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 800, color: '#e9b565', whiteSpace: 'nowrap' }}>
        {icon ? `${icon} ` : ''}{name}
      </span>
      {presence && (
        <span style={{ fontSize: 12.5, color: '#d9cdbb', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: compact ? 'normal' : 'nowrap', flex: compact ? '1 1 100%' : '0 1 auto' }}>
          {presence}
        </span>
      )}
      {/* 📅 countdown band (09-19, calendar D2-C): absent until a night is
          close (utils/calendarView.plateBand). A BAND, never a clock. */}
      {band && band.text && (
        <span data-plate-band="1" style={{
          fontSize: 11.5, fontWeight: 700, padding: '2px 9px', borderRadius: 999, lineHeight: 1.5,
          whiteSpace: compact ? 'normal' : 'nowrap', boxSizing: 'border-box', maxWidth: '100%',
          color: band.hot ? '#ffd98a' : '#e6dac6',
          border: `1px solid ${band.hot ? '#e9b565' : 'rgba(122,96,60,0.55)'}`,
          background: band.hot ? 'rgba(233,181,101,0.16)' : 'rgba(47,35,24,0.9)',
        }}>
          {band.text}
        </span>
      )}
      {!compact && <span style={{ flex: 1 }} />}
      {verbs.map(chip)}
      {/* 👁 09-23 (Nina: option A): the peek chip is RETIRED — /look IS the
          picture now (the caller opens the merged lightbox; rooms with no
          painting fall back to the quiet note). */}
      <button type="button" style={verbPill} title="Look around (only you see this)" onClick={onLook}>/look</button>
      <button type="button" style={{ ...pill, fontWeight: 700 }} onClick={onBack}>← main room</button>
      {toggle}
      {/* 🚶 presence change (09-17 eve): "Fen wanders in." / "Marlow drifts off
          toward the kitchen." — its own full-width line under the row, fades
          on its own (the caller clears it after a minute). */}
      {note && (
        <span aria-live="polite" style={{ flex: '1 1 100%', fontSize: 12, fontStyle: 'italic', color: '#c9b892', lineHeight: 1.4 }}>
          {note}
        </span>
      )}
    </div>
  );
}
